// Mathpix client for extracting structured question data from images
// Requires REACT_APP_MATHPIX_APP_ID and REACT_APP_MATHPIX_APP_KEY in .env
const MATHPIX_API_URL = process.env.REACT_APP_MATHPIX_API_URL || 'https://api.mathpix.com/v3/text';
const APP_ID = process.env.REACT_APP_MATHPIX_APP_ID;
const APP_KEY = process.env.REACT_APP_MATHPIX_APP_KEY;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || '';
      const base64 = typeof result === 'string' ? result.split(',')[1] : '';
      resolve(base64 || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const encodeSvgToDataUrl = (svg) => {
  if (!svg) return null;
  try {
    if (typeof window !== 'undefined' && window.btoa) {
      return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(svg)))}`;
    }
    if (typeof Buffer !== 'undefined') {
      return `data:image/svg+xml;base64,${Buffer.from(svg).toString('base64')}`;
    }
    return null;
  } catch (_) {
    return null;
  }
};

const extractDiagram = (data = {}) => {
  const svg =
    data.diagram_svg ||
    data.diagramSvg ||
    (data.data && (data.data.diagram_svg || data.data.diagramSvg)) ||
    (Array.isArray(data.diagrams) && data.diagrams[0]?.svg);
  return encodeSvgToDataUrl(svg);
};

const parseStructuredQuestionFromText = (text = '') => {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const options = [];
  const questionLines = [];
  const solutionLines = [];
  let correctAnswerRaw = '';
  let inSolution = false;

  lines.forEach((line) => {
    if (/^(solution|soln)\s*[:\-\.]/i.test(line)) {
      inSolution = true;
      const cleaned = line.replace(/^(solution|soln)\s*[:\-\.]\s*/i, '').trim();
      if (cleaned) solutionLines.push(cleaned);
      return;
    }

    const answerMatch = line.match(/^(answer|ans\.?|correct answer)\s*[:\-\.]\s*(.+)$/i);
    if (answerMatch) {
      correctAnswerRaw = answerMatch[2].trim();
      return;
    }

    const optionMatch = line.match(/^(?:[A-D]|[1-4])[\)\.\-:\]]\s*(.+)$/i);
    if (optionMatch) {
      options.push(optionMatch[1].trim());
      return;
    }

    if (inSolution) {
      solutionLines.push(line);
    } else {
      questionLines.push(line);
    }
  });

  const correctAnswerIndex = (() => {
    if (!correctAnswerRaw) return null;
    const firstChar = correctAnswerRaw.trim()[0];
    if (!firstChar) return null;

    const letter = firstChar.toUpperCase();
    if (['A', 'B', 'C', 'D'].includes(letter)) {
      return letter.charCodeAt(0) - 65;
    }

    const asNumber = parseInt(firstChar, 10);
    if (!Number.isNaN(asNumber)) {
      const index = asNumber - 1;
      if (index >= 0 && index < options.length) return index;
    }
    return null;
  })();

  return {
    questionText: questionLines.join('\n'),
    options,
    solutionText: solutionLines.join('\n'),
    correctAnswerIndex,
    correctAnswerText: correctAnswerRaw,
  };
};

const callMathpix = async (src) => {
  if (!APP_ID || !APP_KEY) {
    throw new Error('Mathpix credentials are missing. Set REACT_APP_MATHPIX_APP_ID and REACT_APP_MATHPIX_APP_KEY.');
  }

  const payload = {
    src,
    formats: ['text', 'latex_styled', 'markdown', 'asciimath', 'data'],
    data_options: { include_diagrams: true },
  };

  const response = await fetch(MATHPIX_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      app_id: APP_ID,
      app_key: APP_KEY,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Mathpix request failed: ${message || response.statusText}`);
  }

  const result = await response.json();
  const parsed = parseStructuredQuestionFromText(result.text || result.plaintext || '');
  const diagramImageUrl = extractDiagram(result);

  return {
    raw: result,
    questionText: parsed.questionText,
    options: parsed.options,
    solutionText: parsed.solutionText,
    correctAnswerIndex: parsed.correctAnswerIndex,
    correctAnswerText: parsed.correctAnswerText,
    latex: result.latex_styled || result.latex || '',
    diagramImageUrl,
    mathpixMarkdown: result.markdown || result.mathpix_markdown || '',
  };
};

export const parseQuestionWithMathpix = async (file) => {
  if (!file) throw new Error('No file provided for Mathpix import.');

  const base64 = await fileToBase64(file);
  if (!base64) {
    throw new Error('Could not read the selected file. Please try another image.');
  }

  const src = `data:${file.type};base64,${base64}`;
  return callMathpix(src);
};

export const parseQuestionWithMathpixFromDataUrl = async (dataUrl) => {
  if (!dataUrl) throw new Error('No dataUrl provided for Mathpix import.');
  return callMathpix(dataUrl);
};

